/**
 * Frosted glass surfaces.
 *
 * What separates convincing glass from a translucent grey box is three things
 * working together: a heavy blur with a little extra saturation (so the color
 * behind comes through as a soft wash rather than going muddy), a one pixel
 * hairline edge, and a faint highlight along the top inside edge, which is
 * how real glass catches light. Drop any one of them and it reads cheap.
 *
 * Glass is decoration, so every recipe falls back to a solid surface when it
 * would hurt someone:
 *
 * - Browsers without backdrop-filter get a solid fill instead of a see-through
 *   panel with nothing blurred behind it, which would put text over a busy
 *   photograph with no scrim.
 * - `prefers-reduced-transparency` is an operating system setting people use
 *   precisely because translucent surfaces are hard for them to read.
 * - The accessibility widget's high-contrast mode adds `high-contrast` to
 *   <body>. Translucency lowers contrast by definition, so it goes solid there.
 *
 * Use with Chakra's `sx` prop.
 */

const INK = '14, 47, 58'; // brand.900, the deep teal ink

const solidLight = {
  bg: '#ffffff',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
};

const solidDark = {
  bg: `rgb(${INK})`,
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
};

const fallbacks = (solid) => ({
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': solid,
  '@media (prefers-reduced-transparency: reduce)': solid,
  'body.high-contrast &': { ...solid, borderColor: 'currentColor' },
});

/** Light frosted panel. Dark text sits on it at full contrast. */
export const lightGlass = {
  bg: 'rgba(255, 255, 255, 0.72)',
  backdropFilter: 'blur(20px) saturate(160%)',
  WebkitBackdropFilter: 'blur(20px) saturate(160%)',
  border: '1px solid rgba(255, 255, 255, 0.65)',
  boxShadow: [
    'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
    `0 1px 2px rgba(${INK}, 0.06)`,
    `0 18px 40px -18px rgba(${INK}, 0.28)`,
  ].join(', '),
  ...fallbacks(solidLight),
};

/**
 * Clear smoked glass for small elements sitting directly on photography:
 * chips and secondary buttons. White text on it relies on the hero scrim
 * underneath for contrast, so never place it over an unscrimmed image.
 */
export const clearGlass = {
  bg: 'rgba(255, 255, 255, 0.10)',
  backdropFilter: 'blur(14px) saturate(140%)',
  WebkitBackdropFilter: 'blur(14px) saturate(140%)',
  border: '1px solid rgba(255, 255, 255, 0.24)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.18)',
  ...fallbacks(solidDark),
};
